import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Download, Route, Users2, Phone, MapPin, Building2, FileSignature, Share2 } from "lucide-react";

const NAVY = "#0F2C59";
const PRED_LABEL = { ULASILAMADI: "Ulaşılamadı", BOS: "İşaretsiz", GELMEYECEK: "Gelmeyecek" };
const REASON = {
  person: { l: "Ortak kişi", icon: Users2 },
  phone: { l: "Aynı telefon", icon: Phone },
  address: { l: "Aynı adres", icon: MapPin },
  title: { l: "Unvan grubu", icon: Building2 },
  sicil: { l: "Aynı sicil kökü", icon: FileSignature },
};

export const UnreachedBridges = ({ canExport, onOpenFirm }) => {
  const [data, setData] = useState(null);
  const [committee, setCommittee] = useState("all");
  const [branch, setBranch] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    setData(null);
    const params = {};
    if (committee !== "all") params.committee_no = Number(committee);
    if (branch !== "all") params.branch = branch;
    api.get("/opportunities/network/unreached", { params })
      .then(({ data }) => setData(data))
      .catch(() => { setData({ rows: [], total: 0 }); toast.error("Rapor getirilemedi"); });
  }, [committee, branch]);

  const download = async () => {
    try {
      const params = {};
      if (committee !== "all") params.committee_no = Number(committee);
      if (branch !== "all") params.branch = branch;
      const res = await api.get("/opportunities/network/unreached/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ulasilamayanlar_kopru.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarılamadı");
    }
  };

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const term = q.trim().toLocaleLowerCase("tr");
    if (!term) return data.rows;
    return data.rows.filter((r) => (r.company_name || "").toLocaleLowerCase("tr").includes(term)
      || r.bridges.some((b) => (b.company_name || "").toLocaleLowerCase("tr").includes(term)
        || b.reasons.some((x) => String(x.label || "").toLocaleLowerCase("tr").includes(term))));
  }, [data, q]);
  const ubT = useTableSort(rows);

  if (!data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-4" data-testid="unreached-bridges">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Route className="h-4 w-4" style={{ color: NAVY }} />
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Ulaşılamayanlar · Köprü Bağlantılar</p>
          <Badge variant="outline" className="text-xs" data-testid="ub-total">{data.total} firma</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={committee} onValueChange={setCommittee}>
            <SelectTrigger className="h-9 w-60 text-xs" data-testid="ub-committee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tüm komiteler</SelectItem>
              {(data.committees || []).map((c) => (
                <SelectItem key={c.committee_no} value={String(c.committee_no)}>{c.committee_no}. {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="h-9 w-44 text-xs" data-testid="ub-branch"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tüm şubeler</SelectItem>
              {(data.branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Firma, köprü firma veya kişi ara..." value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="ub-search" />
          </div>
          {canExport && (
            <Button variant="outline" size="sm" className="h-9" onClick={download} data-testid="ub-export">
              <Download className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Ulaşılamayan / işaretsiz / gelmeyecek FAAL firmalar, bizde KESİN ya da yetki belgesi olan bağlantılı firmalarıyla
          birlikte listelenir. En çok köprüsü olanlar üstte.
        </p>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="ub-table">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <ubT.Th sortKey="ub-firma" get={(r) => r.company_name} text className="text-left font-medium py-2 px-3">Firma</ubT.Th>
              <ubT.Th sortKey="ub-komite" get={(r) => r.committee_no} className="text-left font-medium py-2 px-3">Komite / Şube</ubT.Th>
              <ubT.Th sortKey="ub-durum" get={(r) => r.prediction || ""} text className="text-center font-medium py-2 px-3">Durum</ubT.Th>
              <ubT.Th sortKey="ub-sorumlu" get={(r) => r.responsible} text className="text-left font-medium py-2 px-3">Sorumlu</ubT.Th>
              <ubT.Th sortKey="ub-kopru" get={(r) => (r.bridges || []).length} className="text-center font-medium py-2 px-3">Köprü</ubT.Th>
              <th className="text-left font-medium py-2 px-3">Ulaşım Yolu</th>
            </tr>
          </thead>
          <tbody>
            {ubT.sorted.map((r) => (
              <tr key={r.voter_id} className="border-t border-border/50 hover:bg-muted/30" data-testid={`ub-row-${r.voter_id}`}>
                <td className="py-2 px-3 font-medium align-top">
                  <button className="text-left underline decoration-dotted decoration-muted-foreground/50 hover:text-[#0F2C59]"
                    onClick={() => onOpenFirm?.(r.voter_id)} data-testid={`ub-open-${r.voter_id}`}>
                    {r.company_name}
                  </button>
                </td>
                <td className="py-2 px-3 text-muted-foreground align-top whitespace-nowrap">#{r.committee_no ?? "—"} · {r.branch}</td>
                <td className="py-2 px-3 text-center align-top">{PRED_LABEL[r.prediction] || r.prediction}</td>
                <td className="py-2 px-3 text-muted-foreground align-top">{r.responsible || "—"}</td>
                <td className="py-2 px-3 text-center align-top font-semibold">{r.bridge_count}</td>
                <td className="py-2 px-3 align-top">
                  <div className="space-y-1">
                    {r.bridges.map((b) => (
                      <div key={b.voter_id} className="leading-snug">
                        <button className="font-medium text-left underline decoration-dotted decoration-muted-foreground/50 hover:text-[#0F2C59]"
                          onClick={() => onOpenFirm?.(b.voter_id)} data-testid={`ub-bridge-${b.voter_id}`}>
                          {b.company_name}
                        </button>
                        {b.proxy_document && <span className="ml-1 text-emerald-600">· belge var</span>}
                        {b.responsible ? <span className="text-muted-foreground"> · {b.responsible}</span> : null}
                        {b.phone ? <span className="text-muted-foreground"> · {b.phone}</span> : null}
                        <span className="ml-1 inline-flex flex-wrap gap-1">
                          {b.reasons.slice(0, 2).map((x, i) => {
                            const meta = REASON[x.type] || { l: x.type, icon: Share2 };
                            const Icon = meta.icon;
                            return (
                              <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[11px]">
                                <Icon className="h-3 w-3 opacity-70" />{meta.l}{x.label ? `: ${String(x.label).slice(0, 24)}` : ""}
                              </span>
                            );
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">Köprüsü olan ulaşılamayan firma bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
